// src/contexts/ActivityContext.tsx
import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
} from "react";
import { Activity } from "../models/Activity";
import { useAuth } from "./AuthContext";
import { useMatter } from "./MatterContext";
import { MatterService } from "../services/MatterService";

interface ActivityContextProps {
  activities: Activity[];
  loading: boolean;
  error: string | null;
}

const ActivityContext = createContext<ActivityContextProps | undefined>(
  undefined,
);

export const useActivity = (): ActivityContextProps => {
  const context = useContext(ActivityContext);
  if (!context) {
    throw new Error("useActivity must be used within an ActivityProvider");
  }
  return context;
};

export const ActivityProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const { user } = useAuth();
  const { currentMatterId } = useMatter();
  const [activities, setActivities] = useState<Activity[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchActivities = async () => {
      if (!user || !currentMatterId) return;
      setLoading(true);
      setError(null);
      try {
        const fetchedActivities =
          await MatterService.getActivities(currentMatterId);
        setActivities(fetchedActivities);
      } catch (err: any) {
        console.error("Failed to fetch activities:", err);
        setError(err.message || "Failed to fetch activities.");
      } finally {
        setLoading(false);
      }
    };

    fetchActivities();
  }, [user, currentMatterId]);

  return (
    <ActivityContext.Provider
      value={{
        activities,
        loading,
        error,
      }}
    >
      {children}
    </ActivityContext.Provider>
  );
};
